import { useSimplePdp } from "@/lib/preFetch/preFetchPdp"
import { usePromotionTag } from "@/lib/preFetch/prefetchPromoTag"
import prismicColors from "@/lib/prismicio/prismic-colors.json"
import {
    ColorCircle,
    Link,
    ListerTileImage,
    ListerTileProps,
    NextImage,
    PromotionalImage,
    cn,
    getColor,
    getProductUrl,
    getTweakwiseGrid,
    transMoney,
    useGallery,
    useI18n,
    useTile,
} from "@jog/react-components"
import { colorLanguageConverter, getGtmIdFromTW } from "@jog/shared"
import { TweakwiseItem } from "@jog/types"
import classNames from "classnames"
import { type CSSProperties, Fragment } from "react"

import { Favorite } from "../Favorite"

const Discount = ({
    product: { isDiscount, firstPrice, lowestPrice, price },
}: {
    product: ReturnType<typeof useTile>["product"]
}) => {
    const { $t } = useI18n()
    if (!isDiscount) return <></>
    return (
        <div className="mt-1 text-10 text-grey-dark">
            <p>{$t(`Original price: {price}`, { price: transMoney(+firstPrice * 100, { EUR: false }) })}</p>
            {price === lowestPrice || lowestPrice === +firstPrice ? null : (
                <p>
                    {$t(`Lowest price last 30 days: {price}`, { price: transMoney(lowestPrice * 100, { EUR: false }) })}
                </p>
            )}
        </div>
    )
}
const Price = ({ product: { isDiscount, price } }: { product: ReturnType<typeof useTile>["product"] }) => (
    <div
        className={classNames(
            isDiscount ? "text-red-main" : "text-black",
            "mt-1 font-primary text-13 font-medium leading-4"
        )}>
        {transMoney(price * 100)}
    </div>
)
interface CircleStyle extends CSSProperties {
    ["--backgroundColor"]: string
}

const Group = ({
    data,
    onClick,
    tile: { group, hasGroup, setActiveProduct },
}: {
    data: TweakwiseItem
    onClick: (item: TweakwiseItem) => void
    tile: Omit<ReturnType<typeof useTile>, "product" | "promotion">
}) => {
    if (!hasGroup) return <></>
    return (
        <div className="z-[2] -mr-1 mt-1 flex h-5 max-w-2/3 items-center lg:-mr-1.5">
            {group.slice(0, 4).map((i) => {
                const color = getColor(i)
                const circleStyle: CircleStyle = {
                    "--backgroundColor": colorLanguageConverter(color, prismicColors.colors),
                    colorScheme: "only light",
                }
                const label = `${i.title} ${color}`
                const href = getProductUrl(i)
                const commonClass = `items-center justify-center`
                return (
                    <Fragment key={`${data.itemno}_${i.itemno}`}>
                        {/*mobile*/}
                        <Link
                            href={href}
                            className={cn(commonClass, "flex lg:hidden")}
                            aria-label={label}
                            onClick={(e) => {
                                e.stopPropagation()
                                onClick?.(i)
                            }}>
                            <ColorCircle
                                className="fill-[var(--backgroundColor)] stroke-grey-lighter"
                                style={circleStyle}
                            />
                        </Link>
                        {/*desktop*/}
                        <Link
                            href={href}
                            className={cn(commonClass, "hidden lg:flex")}
                            aria-label={label}
                            onMouseEnter={() => setActiveProduct(i)}
                            onMouseLeave={() => setActiveProduct(data)}
                            onClick={(e) => {
                                e.stopPropagation()
                                onClick?.(i)
                            }}>
                            <ColorCircle
                                className="fill-[var(--backgroundColor)] stroke-grey-lighter"
                                style={circleStyle}
                            />
                        </Link>
                    </Fragment>
                )
            })}
            {group.length > 4 && <div className="mx-1.5 flex text-xs">{`+${group.length - 4}`}</div>}
        </div>
    )
}

export const ListerTile = ({ data, className, group: _group, onClick }: ListerTileProps) => {
    const { product, promotion, ...tile } = useTile(data, _group)
    const promotionTag = usePromotionTag(promotion)
    const { mobileSortingByName, desktopSortingByName } = useSimplePdp()
    const gallery = useGallery(tile.activeProduct, mobileSortingByName, desktopSortingByName)

    // id={`item_${data.itemno}`} is for scroll to item, check useLastLister.tsx
    return (
        <div className={classNames(className, "relative flex h-full flex-col bg-white")} id={`item_${data.itemno}`}>
            <div className="group relative">
                <ListerTileImage
                    name={product.name}
                    gallery={gallery}
                    onClick={() => onClick?.(data)}
                    href={product.linkItem}
                />
                <PromotionalImage
                    className="absolute top-2.5 right-0 w-17.5 object-cover"
                    promotionTag={promotionTag}
                />
                <Link
                    onClick={() => onClick?.(data)}
                    href={product.linkItem}
                    className="absolute inset-0 hidden lg:block"
                    aria-label={product.name}
                />
                <Favorite
                    productId={data.itemno}
                    name={product.name}
                    price={product.price}
                    gtmId={getGtmIdFromTW(data)}
                />
            </div>
            <div className="relative">
                <div className="font-base line-clamp-2 relative mt-2 p-0 font-primary text-xs font-medium capitalize text-black lg:line-clamp-none">
                    {product.name}
                </div>
                <div className="mt-1 flex items-center justify-between">
                    <Price product={product} />
                    <Group tile={tile} onClick={onClick} data={data} />
                </div>
                <Discount product={product} />
                <Link
                    href={product.linkItem}
                    className="absolute inset-0"
                    aria-label={product.name}
                    onClick={() => onClick?.(data)}
                />
            </div>
        </div>
    )
}

export const VisualTile = ({ item }: { item: TweakwiseItem }) => {
    const { grid, aspectRatio } = getTweakwiseGrid(item)
    return (
        <div className={grid}>
            <Link
                href={item.url}
                className={cn("flex h-full w-full flex-col", "md:aspect-[var(--desktop-aspect-ratio)]")}
                aria-label={item.title}
                style={aspectRatio}>
                <NextImage alt="" height={400} width={2000} className="h-full w-full object-cover" src={item.image} />
            </Link>
        </div>
    )
}
